import {
  currencyFactorForFormattedAmount,
  getConfigForCurrency,
  roundAmount
} from "components/FormerEditor/common/currency";
import { formatUTCTime } from "utils/date";
import orderBy from "lodash/orderBy";
import get from "lodash/get";
import SERVICES from "utils/constants/services";
import { PRODUCT, PRICE } from "utils/constants/models";
import { SHOPIFY_PRODUCT_STATUS } from "utils/constants/integration/shopify";
import {
  INTEGRATION_ID,
  INTEGRATION_MODEL_ADMIN_URL,
  INTEGRATION_MODEL,
  INTEGRATION_MODEL_UID,
  INTEGRATION_PARENT_MODEL,
  INTEGRATION_PARENT_MODEL_UID,
  INTEGRATION_SERVICE,
  INTEGRATION_SERVICE_UID
} from "utils/constants/metadata";
import { matchingImport } from ".";

export const findProductForUID = ({ products, uid }) =>
  products.find(({ id }) => id === parseInt(uid));

const SHOPIFY_ROOT = ".myshopify.com";
const getProductAdminUrl = ({ shop, product }) =>
  `https://${shop.domain}/admin/products/${product.id}`;

export const getShopifyShopDomain = (shop) => `https://${shop}${SHOPIFY_ROOT}`;

export const getShopifyShop = (shop) => `${shop}${SHOPIFY_ROOT}`;

const getServiceModelMetadata = ({
  integration,
  shop,
  product,
  modelUID,
  service,
  model
}) => ({
  [INTEGRATION_ID]: integration.id,
  [INTEGRATION_SERVICE]: service,
  [INTEGRATION_SERVICE_UID]: shop.id,
  [INTEGRATION_MODEL_UID]: modelUID,
  [INTEGRATION_MODEL]: model,
  [INTEGRATION_MODEL_ADMIN_URL]: getProductAdminUrl({ shop, product })
});

export const prepareShopifyProduct = ({
  shop,
  product,
  merchantProducts,
  integration
}) => {
  const importMatch = matchingImport({
    merchantProducts,
    product,
    integration
  });
  const currencyConfig = getConfigForCurrency(shop.currency);
  const adminUrl = getProductAdminUrl({ shop, product });

  const prices = product.variants.map((variant) => {
    const formattedPrice = `${currencyConfig.symbol}${variant.price}`;

    return {
      formattedPrice,
      currency: shop.currency.toLowerCase(),
      inventory: variant.inventory_quantity,
      // TODO: determine how shopify products can be recurring?
      recurring: null,
      metadata: {
        [INTEGRATION_PARENT_MODEL_UID]: product.id,
        [INTEGRATION_PARENT_MODEL]: PRODUCT,
        ...getServiceModelMetadata({
          integration,
          shop,
          // Shopify variant (price) id
          modelUID: variant.id,
          product,
          service: SERVICES.SHOPIFY,
          model: PRICE
        })
      }
    };
  });

  /**
   * Show the range of prices of there are more than one
   * Single: $10
   * Multiple: $10 - $40
   */
  let priceRange;
  if (prices.length > 1) {
    const orderedPrices = orderBy(prices, "amount");
    priceRange = [
      orderedPrices[0].formattedPrice,
      orderedPrices.pop().formattedPrice
    ].join(" - ");
  } else {
    priceRange = prices[0].formattedPrice;
  }

  const result = {
    id: product.id,
    vendor: product.vendor,
    images: product.images.map(({ src }) => src.split("?")[0]),
    name: product.title,
    service: SERVICES.SHOPIFY,
    metadata: getServiceModelMetadata({
      integration,
      shop,
      // Shopify product id
      modelUID: product.id,
      product,
      service: SERVICES.SHOPIFY,
      model: PRODUCT
    }),
    createdAt: formatUTCTime(product.created_at),
    adminUrl,
    priceRange,
    prices,
    imported: Boolean(importMatch)
  };

  const image = get(product, "image.src", "").split("?")[0];
  result.image = image;

  return result;
};

export const prepareExportToShopifyProduct = ({
  merchantAccountUUID,
  product: { id, name, active, images, prices }
}) => {
  const newProduct = {
    title: name,
    status: active
      ? SHOPIFY_PRODUCT_STATUS.ACTIVE
      : SHOPIFY_PRODUCT_STATUS.DRAFT,
    variants: prices.map((price) => {
      const factor = currencyFactorForFormattedAmount(price.currency);
      const amountToFormat = price.unit_amount * factor;
      const result = {
        price: `${roundAmount(amountToFormat)}`,
        sku: price.id,
        title: price.id,
        option1: price.id
      };

      if (price.recurring) {
        result.metafields = [
          {
            key: "interval",
            value: price.recurring.interval,
            value_type: "string",
            namespace: "priceblocs"
          }
        ];
      }

      return result;
    })
  };
  if (images) {
    newProduct.images = images.map((prodImage) => ({
      src: prodImage
    }));
  }
  return {
    product: newProduct,
    merchantAccount: {
      uuid: merchantAccountUUID,
      model: PRODUCT,
      model_uid: id,
      service: SERVICES.STRIPE
    }
  };
};
